<template>
  <div class="fleet-inventory">
    <header class="fleet-inventory__head">
      <div>
        <h1>Software inventory</h1>
        <p>
          Search every component installed across the estate — plugins, themes,
          add-ons, packages, runtimes and lockfile dependencies — and see which
          site runs it, at what version, on which box, with what PHP.
        </p>
      </div>
    </header>

    <p v-if="loadError" class="fleet-inventory__error">{{ loadError }}</p>

    <form class="fleet-inventory__search" @submit.prevent="search">
      <input
        v-model="query"
        class="fleet-inventory__input"
        type="search"
        placeholder="Component name, e.g. gravityformssmtp"
        aria-label="Component name"
      />
      <select v-model="ecosystem" class="fleet-inventory__select" aria-label="Ecosystem">
        <option value="">All ecosystems</option>
        <option v-for="option in ECOSYSTEMS" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <input
        v-model="version"
        class="fleet-inventory__input fleet-inventory__input--narrow"
        type="text"
        placeholder="Exact version"
        aria-label="Exact version"
      />
      <button class="fleet-inventory__button" type="submit" :disabled="loading">
        {{ loading ? 'Searching…' : 'Search' }}
      </button>
    </form>

    <!--
      Coverage sits ABOVE the results, always. "No results" from a fleet whose
      agents stopped reporting is not the same claim as "no results" from a
      fresh one, and only the denominator makes those distinguishable.
    -->
    <div class="fleet-inventory__coverage" :class="{ 'is-degraded': coverageDegraded }">
      <strong>{{ coverage.servers_current ?? 0 }}/{{ coverage.servers_total ?? 0 }}</strong>
      servers inventoried within {{ staleWindowLabel }}
      <template v-if="coverage.servers_stale"> · {{ coverage.servers_stale }} stale</template>
      <template v-if="coverage.servers_never_inventoried">
        · {{ coverage.servers_never_inventoried }} never reported
      </template>
      <template v-if="coverage.sites_known"> · {{ coverage.sites_known }} sites known</template>
      <span v-if="coverageDegraded" class="fleet-inventory__coverage-warn">
        Results are incomplete while any server is stale — a component installed on
        an unreported box will not appear here.
      </span>
    </div>

    <div v-if="hasSearched" class="fleet-inventory__summary">
      <div class="fleet-inventory__sum">
        <div class="fleet-inventory__sum-value">{{ summary.matches ?? 0 }}</div>
        <div class="fleet-inventory__sum-label">Matches</div>
      </div>
      <div class="fleet-inventory__sum">
        <div class="fleet-inventory__sum-value">{{ summary.sites ?? 0 }}</div>
        <div class="fleet-inventory__sum-label">Sites</div>
      </div>
      <div class="fleet-inventory__sum">
        <div class="fleet-inventory__sum-value">{{ summary.distinct_versions ?? 0 }}</div>
        <div class="fleet-inventory__sum-label">Distinct versions</div>
      </div>
      <div
        class="fleet-inventory__sum"
        :class="{ 'fleet-inventory__sum--bad': (summary.unknown_versions ?? 0) > 0 }"
      >
        <div class="fleet-inventory__sum-value">{{ summary.unknown_versions ?? 0 }}</div>
        <div class="fleet-inventory__sum-label">Unknown versions</div>
        <div class="fleet-inventory__sum-sub">present, version unreadable</div>
      </div>
    </div>

    <section class="fleet-inventory__panel">
      <header class="fleet-inventory__panel-head">
        Results
        <span v-if="summary.truncated" class="fleet-inventory__truncated">
          truncated — narrow the search
        </span>
      </header>

      <p v-if="!hasSearched" class="fleet-inventory__empty">
        Enter a component name to search. An unfiltered listing of every package on
        the estate is deliberately not one click away.
      </p>
      <p v-else-if="loading && !rows.length" class="fleet-inventory__empty">Searching…</p>
      <p v-else-if="!rows.length" class="fleet-inventory__empty">
        No component matched. Check the coverage line above before reading this as
        “not installed anywhere”.
      </p>

      <div v-else class="fleet-inventory__table-wrap">
        <table class="fleet-inventory__table">
          <thead>
            <tr>
              <th>Component</th>
              <th>Version</th>
              <th>Site</th>
              <th>Server</th>
              <th>PHP</th>
              <th>Exposure</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.item_id">
              <td>
                <span class="fleet-inventory__identifier">{{ row.identifier }}</span>
                <span class="fleet-inventory__meta">
                  {{ row.ecosystem }}<template v-if="row.type"> · {{ row.type }}</template>
                  <template v-if="row.name"> · {{ row.name }}</template>
                </span>
              </td>
              <td>
                <span v-if="row.version_known" class="fleet-inventory__version">{{ row.version }}</span>
                <span v-else class="fleet-inventory__pill is-warn">Unknown</span>
              </td>
              <td>
                <template v-if="row.site">{{ row.site }}</template>
                <span v-else class="fleet-inventory__meta">host scope</span>
              </td>
              <td>
                <router-link :to="row.href">{{ row.server }}</router-link>
              </td>
              <td>{{ row.php_version || '—' }}</td>
              <td>
                <span
                  v-if="row.internet_facing"
                  class="fleet-inventory__pill"
                  :class="facingTone(row.internet_facing)"
                >
                  {{ row.internet_facing }}
                </span>
                <span v-else class="fleet-inventory__meta">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script setup>
/**
 * Fleet-wide software inventory — mounted at `/inventory` by
 * `app\module\observability\Controller\FleetDisplayController::inventory()`.
 *
 * This is Plan 010 Phase 1's exit criterion made clickable: "which sites run
 * `gravityformssmtp`, at what version, on which box, with what PHP?" — the
 * question that was answered by hand on 2026-08-06 with a grep and came back
 * confidently, precisely wrong, because the vulnerable component was a
 * separate add-on plugin sharing only a name prefix.
 *
 * Three deliberate choices:
 *
 *  - **Coverage renders above the results, unconditionally.** A search that
 *    returns nothing from a fleet whose agents stopped reporting is not the
 *    same claim as one from a fresh fleet, and a denominator you cannot see is
 *    a denominator you will not question.
 *  - **An unknown version is a badge, never a blank.** "We could not read this
 *    version" and "this version is fine" must never look the same.
 *  - **No polling.** Inventory changes on the timescale of a deploy, not a
 *    request, and this page is a search rather than a monitor — a background
 *    poll would only fight the user's own query.
 */
import { computed, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';

const props = defineProps({
  ...commonProps,
  apiBase: { type: String, default: '/api/v1/observability/fleet' },
});

const ECOSYSTEMS = [
  { value: 'WordPress', label: 'WordPress' },
  { value: 'Drupal', label: 'Drupal' },
  { value: 'Packagist', label: 'Composer (Packagist)' },
  { value: 'npm', label: 'npm' },
  { value: 'deb', label: 'Debian packages' },
  { value: 'rpm', label: 'RPM packages' },
  { value: 'runtime', label: 'Runtimes' },
  { value: 'service', label: 'Services' },
  { value: 'os', label: 'Operating system' },
];

const api = useApi();
const query = ref('');
const ecosystem = ref('');
const version = ref('');
const rows = ref([]);
const summary = ref({});
const coverage = ref({});
const loading = ref(false);
const hasSearched = ref(false);
const loadError = ref(null);

async function load() {
  loading.value = true;
  try {
    const params = new URLSearchParams();
    if (query.value.trim()) params.set('q', query.value.trim());
    if (ecosystem.value) params.set('ecosystem', ecosystem.value);
    if (version.value.trim()) params.set('version', version.value.trim());

    const suffix = params.toString();
    const data = await api.get(`${props.apiBase}/inventory${suffix ? `?${suffix}` : ''}`);
    rows.value = data.rows || [];
    summary.value = data.summary || {};
    coverage.value = data.coverage || {};
    loadError.value = null;
  } catch {
    loadError.value = 'Could not load the software inventory — check your connection and try again.';
  } finally {
    loading.value = false;
  }
}

function search() {
  hasSearched.value = Boolean(query.value.trim() || ecosystem.value || version.value.trim());
  load();
}

// One coverage read on mount so the denominator is on screen before the first
// search, not only after it.
load();

const coverageDegraded = computed(
  () => (coverage.value.servers_stale || 0) > 0 || (coverage.value.servers_never_inventoried || 0) > 0,
);

const staleWindowLabel = computed(() => {
  const seconds = coverage.value.stale_after_seconds || 0;
  if (!seconds) return 'the freshness window';
  const hours = Math.round(seconds / 3600);
  return hours >= 24 ? `${Math.round(hours / 24)}d` : `${hours}h`;
});

function facingTone(facing) {
  if (facing === 'public') return 'is-warn';
  if (facing === 'internal') return 'is-info';
  return 'is-unknown';
}
</script>

<style lang="scss">
.fleet-inventory {
  &__head {
    margin-bottom: 18px;

    h1 { font-size: 20px; font-weight: 600; margin: 0 0 4px; }
    p { font-size: 13px; color: var(--theme-text-secondary); margin: 0; max-width: 74ch; }
  }

  &__error {
    background: #f8dede;
    color: #a53232;
    border-radius: var(--theme-radius, 6px);
    padding: 10px 14px;
    font-size: 12.5px;
    margin-bottom: 14px;
  }

  &__search {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }

  &__input,
  &__select {
    flex: 1 1 200px;
    min-width: 140px;
    padding: 8px 10px;
    font-size: 12.5px;
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius, 6px);
    background: var(--theme-bg-card);
    color: inherit;

    &--narrow { flex: 0 1 150px; }
  }

  &__button {
    flex: none;
    padding: 8px 18px;
    font-size: 12.5px;
    font-weight: 600;
    border: 1px solid var(--theme-border-strong);
    border-radius: var(--theme-radius, 6px);
    background: var(--theme-bg-content);
    cursor: pointer;

    &:disabled { opacity: 0.6; cursor: default; }
  }

  &__coverage {
    font-family: var(--theme-font-mono);
    font-size: 11px;
    color: var(--theme-text-secondary);
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-left: 3px solid var(--theme-border-strong);
    border-radius: var(--theme-radius, 6px);
    padding: 8px 12px;
    margin-bottom: 14px;

    &.is-degraded { border-left-color: var(--theme-status-warning); }
  }

  &__coverage-warn {
    display: block;
    margin-top: 4px;
    color: #8a5a0d;
  }

  &__summary {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 16px;

    @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
  }

  &__sum {
    position: relative;
    overflow: hidden;
    padding: 13px 16px;
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);

    &::before {
      content: "";
      position: absolute;
      left: 0; top: 0; bottom: 0;
      width: 3px;
      background: var(--theme-border-strong);
    }

    &--bad::before { background: var(--theme-status-warning); }
  }

  &__sum-value {
    font-size: 22px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    line-height: 1.2;
  }

  &__sum-label { font-size: 11.5px; color: var(--theme-text-secondary); margin-top: 2px; }
  &__sum-sub {
    font-family: var(--theme-font-mono);
    font-size: 10px;
    color: var(--theme-text-tertiary);
    margin-top: 4px;
  }

  &__panel {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    overflow: hidden;
  }

  &__panel-head {
    padding: 11px 14px;
    border-bottom: 1px solid var(--theme-border);
    font-size: 12.5px;
    font-weight: 620;
  }

  &__truncated {
    margin-left: 8px;
    font-weight: 400;
    font-size: 10.5px;
    color: #8a5a0d;
  }

  &__empty {
    padding: 16px 14px;
    margin: 0;
    font-size: 12.5px;
    color: var(--theme-text-tertiary);
  }

  &__table-wrap { overflow-x: auto; }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;

    th {
      text-align: left;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--theme-text-tertiary);
      font-weight: 600;
      padding: 8px 14px;
      border-bottom: 1px solid var(--theme-border);
      white-space: nowrap;
    }

    td {
      padding: 9px 14px;
      border-bottom: 1px solid var(--theme-border-soft);
      vertical-align: top;
    }

    tr:last-child td { border-bottom: 0; }
  }

  &__identifier { display: block; font-weight: 600; }

  &__meta {
    display: block;
    font-family: var(--theme-font-mono);
    font-size: 10.5px;
    color: var(--theme-text-tertiary);
    margin-top: 2px;
  }

  &__version {
    font-family: var(--theme-font-mono);
    font-size: 11.5px;
  }

  &__pill {
    display: inline-block;
    font-size: 9.5px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    padding: 2.5px 8px;
    border-radius: 20px;
    font-weight: 700;
    white-space: nowrap;

    &.is-warn { background: #fdf1de; color: #8a5a0d; }
    &.is-info { background: var(--theme-border-soft); color: var(--theme-text-secondary); }
    &.is-unknown { background: var(--theme-border-soft); color: var(--theme-text-tertiary); }
  }
}
</style>
